import { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import RentalManagement from '@/components/admin/RentalManagement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminRentalsPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      // Check for admin app bypass key
      const urlParams = new URLSearchParams(window.location.search);
      const adminKey = urlParams.get('adminkey');
      const userAgent = navigator.userAgent;
      
      // Admin app bypass - auto-authenticate if key is valid
      if (adminKey === 'BLCPR_ADMIN_2024' || userAgent.includes('BiggestLittleCPR')) {
        try {
          const response = await axios.post(`${API}/admin/app-login`, { 
            adminkey: adminKey || 'BLCPR_ADMIN_2024',
            user_agent: userAgent
          });
          if (response.data.success) {
            localStorage.setItem('adminToken', response.data.token);
            setIsAuthenticated(true);
            // Remove the key from URL for cleanliness
            window.history.replaceState({}, '', '/admin/rentals');
            setAuthChecking(false);
            return;
          }
        } catch (e) {
          console.log('Admin app login failed:', e);
        }
      }
      
      // Standard token check
      const token = localStorage.getItem('adminToken');
      if (token) {
        try {
          await axios.post(`${API}/admin/verify-token?token=${token}`);
          setIsAuthenticated(true);
        } catch {
          localStorage.removeItem('adminToken');
        }
      }
      setAuthChecking(false);
    };
    checkAuth();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        setIsAuthenticated(true);
      } else {
        setAuthError('Invalid password');
      }
    } catch (error) {
      setAuthError('Login failed');
    }
  };

  if (authChecking) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-xl p-8 w-full max-w-md border border-slate-700">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Package className="h-8 w-8 text-purple-400" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center text-white mb-2">Rental Management</h1>
          <p className="text-slate-400 text-center mb-6">Enter your password to continue</p>
          
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-slate-300 text-sm mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter admin password"
                autoFocus
              />
            </div>
            {authError && (
              <p className="text-red-400 text-sm mb-4">{authError}</p>
            )}
            <Button
              type="submit"
              className="w-full bg-purple-600 hover:bg-purple-500 text-white py-3"
            >
              Sign In
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <a href="/admin" className="text-slate-400 hover:text-teal-400 text-sm">
              ← Back to Admin Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Use the original RentalManagement component with open=true
  // It will show as a full-screen dialog overlay
  return (
    <div className="min-h-screen bg-slate-900">
      <RentalManagement 
        open={true} 
        onClose={() => window.location.href = '/admin'} 
      />
    </div>
  );
};

export default AdminRentalsPage;
